<script setup lang="ts">
/**
 * SectionEyebrow — eyebrow + count + trailing actions row that sits above
 * a list section. Mirrors the Planning screen header row on Android:
 *
 *     SCHEMES OF WORK · 3                          [sort] [+ New]
 *
 * The label is uppercased and tracked wide. The count after the bullet
 * uses the same eyebrow style, so it reads as one continuous label.
 *
 * Use it inside list screens (Classes, Planning) and inside widgets on
 * Home — anywhere a list section needs a header without the visual
 * weight of a full screen header.
 */

defineProps<{
  /** Eyebrow text (rendered uppercased). */
  label: string
  /** Optional count appended after the bullet. Hidden when undefined. */
  count?: number
}>()
</script>

<template>
  <div class="section-eyebrow">
    <p class="section-eyebrow-label">
      <span>{{ label }}</span>
      <span v-if="count !== undefined" aria-hidden="true" class="section-eyebrow-bullet">·</span>
      <span v-if="count !== undefined" class="section-eyebrow-count tnum">{{ count }}</span>
    </p>
    <div v-if="$slots.actions" class="section-eyebrow-actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
.section-eyebrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-4) var(--space-16) var(--space-sm) var(--space-20);
}

.section-eyebrow-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-6);
  font-family: var(--font-sans);
  font-size: var(--text-micro);
  line-height: var(--text-micro-lh);
  letter-spacing: 1.2px;
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  color: var(--ink-600);
  margin: 0;
}

.section-eyebrow-bullet {
  color: var(--ink-400);
}

.section-eyebrow-count {
  color: var(--ink-600);
  font-variant-numeric: tabular-nums;
}

.section-eyebrow-actions {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}
</style>
